import { useQueryClient } from '@tanstack/react-query'
import { CheckSquare, History, LucideIcon, Sparkles, Square } from 'lucide-react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import React, { useCallback, useMemo } from 'react'

import { EscalationIcon } from '@/components/icon/encapsulated/escalation-icon'
import { useTouchable } from '@/components/layout/touchable-context'
import { useToast } from '@/components/ui/use-toast'

import { useNotificationUpdateReadMutation, useNotificationUpdateUnreadMutation } from '@/hooks/query/notification'
import { useAuth } from '@/hooks/use-auth'
import { getNotificationGroupTypeFromType, NOTIFICATION_TYPE, NOTIFICATION_TYPE_GROUP } from '@/lib/notification'
import { cn, convertNonNormalWhitespaceToWhitespace, formatTimestampHistory } from '@/lib/utils'
import { useNotificationStore } from '@/stores/notification.store'
import { AvailableLocale } from '@/types/global'
import { NotificationItem } from '@/types/notification'

type NotificationItemProps = {
  item: NotificationItem
  className?: string
  onClick?: () => void
}

const BASE_STYLE =
  'border-base cursor-pointer border-b bg-base-card p-4 transition-colors hover:bg-accent group/notification-item'
const NotificationItemDisplay = ({ item, className, onClick }: NotificationItemProps) => {
  const tComponent = useTranslations('Notification.components.notification_item_display')
  const locale = useLocale() as AvailableLocale
  const { session } = useAuth()
  const queryClient = useQueryClient()
  const { mutateAsync: updateMutateNotificationAsRead } = useNotificationUpdateReadMutation(queryClient)
  const { mutateAsync: updateMutateNotificationAsUnread } = useNotificationUpdateUnreadMutation(queryClient)
  const { decrementUnreadCount, incrementUnreadCount } = useNotificationStore()
  const { toast } = useToast()
  const { isHoverableOnly } = useTouchable()

  const url = useMemo(() => {
    if (!item.url) return ''

    // if notification is read already, do not attach notificationId
    // to lessen unnecessary mark as read update on notification open
    if (item.isRead) return item.url

    const hasQueryParams = item.url.includes('?')

    return `${item.url}${hasQueryParams ? '&' : '?'}notificationId=${item._id}`
  }, [item.url, item._id, item.isRead])

  const groupType = useMemo(() => {
    return getNotificationGroupTypeFromType(item.type)
  }, [item.type])

  const groupTypeDisplay = useMemo(() => {
    let Icon: LucideIcon
    let text: string
    if (groupType === NOTIFICATION_TYPE_GROUP.TRAINING) {
      Icon = Sparkles
      text = tComponent('group_type.training')
    } else {
      Icon = History
      text = tComponent('group_type.history')
    }
    return (
      <>
        <Icon className="size-3 shrink-0" />
        <p className="text-xs">{text}</p>
      </>
    )
  }, [groupType, tComponent])
  const titleDisplay = useMemo(() => {
    return (
      <>
        {item?.type === NOTIFICATION_TYPE.CHAT_ESCALATION_REQUEST ||
        item?.type === NOTIFICATION_TYPE.CHAT_ESCALATION_RESOLVED ? (
          <EscalationIcon
            className="size-3 shrink-0"
            isResolved={item?.type === NOTIFICATION_TYPE.CHAT_ESCALATION_RESOLVED}
          />
        ) : null}
        <p className="truncate whitespace-pre text-sidebar-foreground">
          {convertNonNormalWhitespaceToWhitespace(item.titleContent ?? '')}
        </p>
      </>
    )
  }, [groupType, item.titleContent])

  const handleReadUnreadToggle = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      try {
        if (item.isRead) {
          await updateMutateNotificationAsUnread({ id: item._id })
          incrementUnreadCount()
        } else {
          await updateMutateNotificationAsRead({
            id: item._id,
          })
          decrementUnreadCount()
        }
      } catch (error) {
        console.error(error)
        toast({
          variant: 'destructive',
          description: tComponent('messages.update_failed'),
        })
      }
    },
    [
      updateMutateNotificationAsRead,
      updateMutateNotificationAsUnread,
      item._id,
      item.isRead,
      incrementUnreadCount,
      decrementUnreadCount,
    ],
  )

  const content = (
    <>
      <div className="mb-2.5 flex items-center gap-2 text-muted-foreground">
        <div
          className={cn('size-2 shrink-0 animate-fade-in rounded-full bg-brand-primary', {
            hidden: item.isRead,
          })}
        />
        <div className="flex flex-1 items-center gap-1">{groupTypeDisplay}</div>

        <div className="ms-auto flex">
          <p
            className={cn('animate-fade-in text-xs', {
              'group-hover/notification-item:hidden': isHoverableOnly,
            })}
          >
            {formatTimestampHistory(item?.createdAt, locale, session?.user?.activeOrganization?.timezone)}
          </p>

          <div
            title={item.isRead ? tComponent('messages.mark_as_unread') : tComponent('messages.mark_as_read')}
            className={cn('ms-2 shrink-0 animate-fade-in cursor-pointer text-base-sidebar-foreground', {
              'hidden group-hover/notification-item:block': isHoverableOnly,
            })}
            onClick={handleReadUnreadToggle}
          >
            {item.isRead ? <Square className="size-4" /> : <CheckSquare className="size-4" />}
          </div>
        </div>
      </div>
      <div className="mb-1 flex items-center gap-2 overflow-hidden" title={item.titleContent}>
        {titleDisplay}
      </div>
      <p
        className="line-clamp-2 hyphens-auto whitespace-pre text-pretty text-xs text-muted-foreground"
        title={item.bodyContent}
      >
        {convertNonNormalWhitespaceToWhitespace(item.bodyContent ?? '')}
      </p>
    </>
  )

  return item.url ? (
    <Link href={url} className={cn(BASE_STYLE, className)} onClick={onClick}>
      {content}
    </Link>
  ) : (
    <div className={cn(BASE_STYLE, className)} onClick={onClick}>
      {content}
    </div>
  )
}
NotificationItemDisplay.displayName = 'NotificationItemDisplay'

export { NotificationItemDisplay }
